import React, { useState } from 'react';
import { db } from '../lib/firebase';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { BrandGradient } from '../components/common/PFILogo';
import PhoneInput from '../components/PhoneInput';
import { 
  EnvelopeIcon,
  PhoneIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

export default function SupplierPortalLogin() {
  const [formData, setFormData] = useState({
    email: '',
    phone: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Basic validation
      if (!formData.email.trim()) {
        setMessage({ type: 'error', text: 'Please enter your email address' });
        return;
      }

      if (!formData.phone.trim()) {
        setMessage({ type: 'error', text: 'Please enter your phone number' });
        return;
      }

      // Look up supplier by email and phone
      const suppliersQuery = query(
        collection(db, 'suppliers'),
        where('email', '==', formData.email.trim().toLowerCase())
      );
      
      const snapshot = await getDocs(suppliersQuery);
      let matchedSupplier = null;

      // Check if phone number matches (handle different formats)
      snapshot.docs.forEach(doc => {
        const supplier = { id: doc.id, ...doc.data() };
        const supplierPhone = supplier.phone || '';
        const enteredPhone = formData.phone.trim();
        
        // Normalize phone numbers for comparison
        const normalizePhone = (phone) => phone.replace(/\D/g, '');
        const normalizedSupplierPhone = normalizePhone(supplierPhone);
        const normalizedEnteredPhone = normalizePhone(enteredPhone);
        
        // Check various phone format matches
        if (normalizedSupplierPhone === normalizedEnteredPhone ||
            normalizedSupplierPhone.endsWith(normalizedEnteredPhone) ||
            normalizedEnteredPhone.endsWith(normalizedSupplierPhone)) {
          matchedSupplier = supplier;
        }
      });

      if (!matchedSupplier) {
        setMessage({ 
          type: 'error', 
          text: 'No supplier found with those credentials. Please check your email and phone number.' 
        });
        return;
      }

      // Generate a fresh magic link token
      const token = generateMagicLinkToken();
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 days
      
      // Create supplier access record
      await addDoc(collection(db, 'supplierAccess'), {
        supplierId: matchedSupplier.id,
        supplierName: matchedSupplier.name,
        supplierEmail: matchedSupplier.email,
        token,
        expiresAt,
        createdAt: new Date(),
        used: false,
        source: 'portal-login'
      });

      // Create notification for email/SMS
      await addDoc(collection(db, 'notifications'), {
        type: 'supplier-portal-access',
        recipientId: matchedSupplier.id,
        recipientEmail: matchedSupplier.email,
        recipientPhone: matchedSupplier.phone,
        recipientName: matchedSupplier.name,
        supplierName: matchedSupplier.name,
        message: `Access link requested for supplier portal`,
        magicLink: `${window.location.origin}/quote/${token}`,
        accessUrl: `${window.location.origin}/quote/${token}`,
        expiresAt,
        status: 'pending',
        createdAt: serverTimestamp()
      });

      setMessage({ 
        type: 'success', 
        text: `Access links sent! Check your email and SMS for the login link. The link will be valid for 7 days.` 
      });
      
      // Clear form
      setFormData({ email: '', phone: '' });

    } catch (error) {
      console.error('Error sending supplier access link:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to send access link. Please try again or contact support.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateMagicLinkToken = () => {
    return Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <BrandGradient className="rounded-none p-8 text-white shadow-xl">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-start">
              <EnvelopeIcon className="w-12 h-12 mr-6 text-white filter drop-shadow-lg" />
              <div className="flex-1">
                <h1 className="text-4xl font-bold mb-3">Supplier Portal Access</h1>
                <p className="text-pink-100 text-xl">Get instant access to your PFI Connect portal</p>
              </div>
            </div>
            <a 
              href="/"
              className="flex items-center text-pink-100 hover:text-white transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Home
            </a>
          </div>
        </div>
      </BrandGradient>

      <div className="max-w-2xl mx-auto p-8">
        <div className="bg-white rounded-2xl shadow-xl border border-pink-100 p-8">
          {/* Info Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Request Portal Access</h2>
            <p className="text-gray-600 mb-4">
              Enter your registered email and phone number below. We'll send you fresh access links 
              via both email and SMS so you can access your supplier portal.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircleIcon className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-blue-800 font-medium mb-1">What you'll receive:</p>
                  <ul className="text-blue-700 space-y-1">
                    <li>• Secure access link via email</li>
                    <li>• Backup SMS with the same link</li>
                    <li>• Access to all your active quotes and job sheets</li>
                    <li>• Links valid for 7 days</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <EnvelopeIcon className="w-4 h-4 inline mr-2" />
                Registered Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="your.email@company.com"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the email address you used when registering with Pink Flamingo Interiors
              </p>
            </div>

            {/* Phone Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <PhoneIcon className="w-4 h-4 inline mr-2" />
                Registered Phone Number *
              </label>
              <PhoneInput
                value={formData.phone}
                onChange={(phone) => setFormData({ ...formData, phone })}
                placeholder="0414 434 999"
                className="w-full"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the phone number associated with your supplier account
              </p>
            </div>

            {/* Message Display */}
            {message.text && (
              <div className={`rounded-lg p-4 ${
                message.type === 'success' 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start">
                  {message.type === 'success' ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                  ) : (
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  )}
                  <p className={`text-sm ${
                    message.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {message.text}
                  </p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full flex items-center justify-center px-6 py-4 rounded-lg font-semibold text-lg transition-colors ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-pink-600 text-white hover:bg-pink-700'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500 mr-3"></div>
                  Sending Access Links...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="w-6 h-6 mr-3" />
                  Send Me Access Links
                </>
              )}
            </button>
          </form>

          {/* Help Section */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Need Help?</h3>
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                <strong>Can't remember your registered details?</strong><br />
                Contact Pink Flamingo Interiors at{' '}
                <a href="mailto:suppliers@pinkflamingo.com.au" className="text-pink-600 hover:text-pink-700">
                  suppliers@pinkflamingo.com.au
                </a>
              </p>
              <p>
                <strong>Changed your phone number or email?</strong><br />
                Please contact us to update your details before requesting access.
              </p>
              <p>
                <strong>Still not receiving the links?</strong><br />
                Check your spam folder and ensure SMS isn't blocked. Links are sent immediately.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}